import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { createChart, LineSeries } from "lightweight-charts";

/* ===== Financial Targets — compounding + withdrawal + loan tracker =====
   Tracks the "seed capital compounding at plan pace, withdraw X% of each
   positive month" plan against the ACTUAL trade book (all live monitor
   trades, P&L booked on exit day). Withdrawals are virtual bookkeeping.
   Optional borrowed-capital layer: a monthly EMI is paid FIRST out of each
   month's withdrawal (shortfall comes out of banked cash, then equity), and
   the tab tracks the loan's outstanding balance and the month when banked
   cash covers foreclosure (outstanding + preclosure fee). A tracker to
   INFORM the borrow decision — not advice. */

const fmt = (v) => `₹${Math.round(Math.abs(v)).toLocaleString("en-IN")}`;
const fmtS = (v) => `${v >= 0 ? "+" : "-"}₹${Math.round(Math.abs(v)).toLocaleString("en-IN")}`;
const dayStr = (d) => d.toLocaleDateString("en-CA");

function* weekdays(fromStr, toStr) {
  const d = new Date(fromStr + "T00:00:00");
  const end = new Date(toStr + "T00:00:00");
  while (d <= end) {
    if (d.getDay() !== 0 && d.getDay() !== 6) yield dayStr(d);
    d.setDate(d.getDate() + 1);
  }
}
const monthOf = (ds) => ds.slice(0, 7);
const monthLabel = (m) => new Date(m + "-01T00:00:00").toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

/* loan math: standard amortization */
const emiOf = (P, annualPct, n) => {
  const r = annualPct / 1200;
  if (!P || !n) return 0;
  if (!r) return P / n;
  const f = Math.pow(1 + r, n);
  return P * r * f / (f - 1);
};
const outstandingAfter = (P, annualPct, n, k, emi) => {
  const r = annualPct / 1200;
  if (k >= n) return 0;
  if (!r) return Math.max(0, P - emi * k);
  const f = Math.pow(1 + r, k);
  return Math.max(0, P * f - emi * (f - 1) / r);
};

function Card({ label, value, sub, accent }) {
  return (
    <div style={{ flex: "1 1 180px", minWidth: 165, background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: accent || "var(--text2)" }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--muted2)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function PlanChart({ series }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !series.actual.length) return;
    const light = document.body.classList.contains("light");
    const col = light
      ? { bg: "#f8fafc", text: "#4c5b68", grid: "#e5eaf0", plan: "#8b5cf6", actual: "#0B8043" }
      : { bg: "#0b1120", text: "#64748b", grid: "#1c2536", plan: "#a78bfa", actual: "#22c55e" };
    const chart = createChart(ref.current, {
      width: ref.current.clientWidth || 500, height: 260,
      layout: { background: { color: col.bg }, textColor: col.text, attributionLogo: false },
      grid: { vertLines: { color: col.grid }, horzLines: { color: col.grid } },
      timeScale: { borderColor: col.grid }, rightPriceScale: { borderColor: col.grid },
    });
    const plan = chart.addSeries(LineSeries, { color: col.plan, lineWidth: 2, lineStyle: 2, title: "plan", priceLineVisible: false });
    plan.setData(series.plan);
    const act = chart.addSeries(LineSeries, { color: col.actual, lineWidth: 2, title: "actual", priceLineVisible: false });
    act.setData(series.actual);
    chart.timeScale().fitContent();
    const sync = () => { if (ref.current) { chart.applyOptions({ width: ref.current.clientWidth }); chart.timeScale().fitContent(); } };
    const ro = new ResizeObserver(sync); ro.observe(ref.current);
    window.addEventListener("resize", sync);
    return () => { ro.disconnect(); window.removeEventListener("resize", sync); chart.remove(); };
  }, [series]);
  if (!series.actual.length) return null;
  return <div ref={ref} data-noswipe style={{ width: "100%", height: 260, borderRadius: 8, overflow: "hidden", border: "1px solid var(--panel3)" }} />;
}

export default function FinancialTargets() {
  const [cfg, setCfg] = useState(null);
  const [trades, setTrades] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState("");

  const load = async () => {
    try {
      const [c, m] = await Promise.all([
        axios.get("/api/financial-targets/config", { timeout: 10000 }),
        axios.get("/api/monitor/status", { timeout: 30000 }),
      ]);
      if (c.data?.success) {
        setCfg(c.data.config);
        setForm({ ...c.data.config, ...(c.data.config.loan ? {
          loanEnabled: c.data.config.loan.enabled, loanPrincipal: c.data.config.loan.principal,
          loanRate: c.data.config.loan.annualRatePct, loanTenure: c.data.config.loan.tenureMonths,
          loanFee: c.data.config.loan.preclosureFeePct } : {}) });
      }
      setTrades((m.data?.trades || []).filter(t => t.status === "CLOSED" && t.netPnl != null && t.exitTime));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        startDate: form.startDate,
        seedCapital: Number(form.seedCapital), withdrawPct: Number(form.withdrawPct),
        dailyPaceGeo: Number(form.dailyPaceGeo), targetEarned: Number(form.targetEarned),
        loan: {
          enabled: !!form.loanEnabled, principal: Number(form.loanPrincipal),
          annualRatePct: Number(form.loanRate), tenureMonths: Number(form.loanTenure),
          preclosureFeePct: Number(form.loanFee),
        },
      };
      const r = await axios.post("/api/financial-targets/config", body);
      if (r.data?.success) { setCfg(r.data.config); setSaveMsg("saved"); setTimeout(() => setSaveMsg(""), 2000); }
    } catch (e) { setSaveMsg(e.message || "save failed"); }
  };

  const model = useMemo(() => {
    if (!cfg) return null;
    const today = dayStr(new Date());
    const seed = cfg.seedCapital, pct = cfg.withdrawPct, pace = cfg.dailyPaceGeo;
    const loan = cfg.loan && cfg.loan.enabled ? cfg.loan : null;
    const emi = loan ? emiOf(loan.principal, loan.annualRatePct, loan.tenureMonths) : 0;

    const dayPnl = {};
    let nTrades = 0, nWins = 0;
    for (const t of trades) {
      const d = dayStr(new Date(t.exitTime));
      if (d < cfg.startDate) continue;
      dayPnl[d] = (dayPnl[d] || 0) + t.netPnl;
      nTrades++; if (t.netPnl > 0) nWins++;
    }
    const started = cfg.startDate <= today;
    const days = started ? [...weekdays(cfg.startDate, today)] : [];

    // day-by-day walk; month close = withdrawal, then EMI paid out of bank
    let eq = seed, planEq = seed, bank = 0, planBank = 0, withdrawn = 0, totalPnl = 0;
    let mPnl = 0, mPlanPnl = 0, payouts = 0, emisPaid = 0;
    const actualSeries = [], planSeries = [], ledger = [];
    const rets = [];
    const closeMonth = (d) => {
      const wd = mPnl > 0 ? mPnl * pct : 0;
      const pwd = mPlanPnl > 0 ? mPlanPnl * pct : 0;
      if (wd > 0) payouts++;
      withdrawn += wd; eq -= wd; bank += wd;
      planEq -= pwd; planBank += pwd;
      let emiDue = 0;
      if (loan && emisPaid < loan.tenureMonths) {
        emiDue = emi; emisPaid++;
        bank -= emiDue;
        if (bank < 0) { eq += bank; bank = 0; }   // shortfall comes out of trading capital
        planBank -= emiDue;
        if (planBank < 0) { planEq += planBank; planBank = 0; }
      }
      const loanOs = loan ? outstandingAfter(loan.principal, loan.annualRatePct, loan.tenureMonths, emisPaid, emi) : 0;
      ledger.push({ month: monthOf(d), pnl: mPnl, wd, emi: emiDue, bank, eqEnd: eq, planEq, loanOs, running: false });
      mPnl = 0; mPlanPnl = 0;
    };
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      const p = dayPnl[d] || 0;
      if (p !== 0 && eq > 0) rets.push(p / eq);
      eq += p; totalPnl += p; mPnl += p;
      const pp = planEq * pace;
      planEq += pp; mPlanPnl += pp;
      if (i < days.length - 1 && monthOf(days[i + 1]) !== monthOf(d)) closeMonth(d);
      actualSeries.push({ time: d, value: Math.round(eq) });
      planSeries.push({ time: d, value: Math.round(planEq) });
    }
    if (days.length) {
      const loanOs = loan ? outstandingAfter(loan.principal, loan.annualRatePct, loan.tenureMonths, emisPaid, emi) : 0;
      ledger.push({ month: monthOf(days[days.length - 1]), pnl: mPnl, wd: null, emi: loan && emisPaid < loan.tenureMonths ? emi : 0, bank, eqEnd: eq, planEq, loanOs, running: true });
    }

    // loan status now
    const loanOsNow = loan ? outstandingAfter(loan.principal, loan.annualRatePct, loan.tenureMonths, emisPaid, emi) : 0;
    const principalPaid = loan ? loan.principal - loanOsNow : 0;
    const interestPaid = loan ? Math.max(0, emisPaid * emi - principalPaid) : 0;
    const fcCost = loan ? loanOsNow * (1 + loan.preclosureFeePct / 100) : 0;
    const foreclosableNow = loan ? bank >= fcCost && loanOsNow > 0 : false;

    // forward sim at ACTUAL geometric pace: target ETA + foreclosure ETA
    let etaTarget = null, etaForeclose = null;
    if (rets.length >= 5) {
      const g = Math.exp(rets.reduce((a, r) => a + Math.log(1 + r), 0) / rets.length) - 1;
      if (g > 0) {
        let e2 = eq, b2 = bank, tp = totalPnl, mp = 0, k = emisPaid, dcount = 0;
        const cur = new Date(today + "T00:00:00");
        while (dcount < 3000 && (tp < cfg.targetEarned || (loan && !etaForeclose))) {
          cur.setDate(cur.getDate() + 1);
          if (cur.getDay() === 0 || cur.getDay() === 6) continue;
          const p = e2 * g; e2 += p; tp += p; mp += p; dcount++;
          if (cur.getDate() >= 28 && mp !== 0) {
            const wd = mp > 0 ? mp * pct : 0;
            e2 -= wd; b2 += wd; mp = 0;
            if (loan && k < loan.tenureMonths) {
              k++; b2 -= emi;
              if (b2 < 0) { e2 += b2; b2 = 0; }
            }
            if (loan && !etaForeclose) {
              const os = outstandingAfter(loan.principal, loan.annualRatePct, loan.tenureMonths, k, emi);
              if (os > 0 && b2 >= os * (1 + loan.preclosureFeePct / 100)) etaForeclose = dayStr(cur);
              if (os <= 0) etaForeclose = "matures " + dayStr(cur);
            }
          }
          if (tp >= cfg.targetEarned && !etaTarget) etaTarget = dayStr(cur);
        }
      }
    }

    const pendingWd = mPnl > 0 ? mPnl * pct : 0;
    const daysLeft = (() => {
      if (!days.length) return 0;
      const last = days[days.length - 1];
      const eom = new Date(last + "T00:00:00"); eom.setMonth(eom.getMonth() + 1); eom.setDate(0);
      let n = 0; const c = new Date(last + "T00:00:00");
      while (c < eom) { c.setDate(c.getDate() + 1); if (c.getDay() !== 0 && c.getDay() !== 6) n++; }
      return n;
    })();
    return { started, eq, bank, withdrawn, totalPnl, nTrades, nWins, ledger, payouts,
             series: { actual: actualSeries, plan: planSeries }, etaTarget, etaForeclose,
             mPnl, pendingWd, daysLeft, planGap: eq + bank - (planEq + planBank),
             loan, emi, emisPaid, loanOsNow, principalPaid, interestPaid, fcCost, foreclosableNow };
  }, [cfg, trades]);

  if (!cfg) return <div className="loading"><div className="spinner" /><p>Loading plan…</p></div>;
  const m = model;
  const progress = Math.max(0, Math.min(1, m.totalPnl / cfg.targetEarned));
  const sizeMult = m.eq / cfg.seedCapital;

  const input = (key, label, type = "number", step) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 10.5, color: "var(--muted2)" }}>
      {label}
      <input className="ema-select" type={type} step={step} value={form[key] ?? ""} style={{ minWidth: 120 }}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </label>
  );

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h2>Financial Targets</h2>
          <p className="dashboard-subtitle">
            Seed {fmt(cfg.seedCapital)} · withdraw {Math.round(cfg.withdrawPct * 100)}% of each positive month · plan pace {(cfg.dailyPaceGeo * 100).toFixed(2)}%/day · target {fmt(cfg.targetEarned)} earned · from {cfg.startDate}
            {m.loan && <> · loan {fmt(m.loan.principal)} @ {m.loan.annualRatePct}% / {m.loan.tenureMonths}m (EMI {fmt(m.emi)})</>}
            <button onClick={() => setEditOpen(o => !o)} style={{ marginLeft: 10, background: "var(--btn-muted)", color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 4, fontSize: 10.5, padding: "2px 10px", cursor: "pointer" }}>
              {editOpen ? "Close" : "Edit plan"}
            </button>
          </p>
        </div>
      </div>

      {editOpen && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
          {input("startDate", "Start date", "date")}
          {input("seedCapital", "Seed capital (₹)")}
          {input("withdrawPct", "Withdraw fraction (0–1)", "number", "0.05")}
          {input("dailyPaceGeo", "Plan pace (daily)", "number", "0.001")}
          {input("targetEarned", "Target earned (₹)")}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--muted2)", paddingBottom: 6 }}>
            <input type="checkbox" checked={!!form.loanEnabled} onChange={e => setForm(f => ({ ...f, loanEnabled: e.target.checked }))} /> Loan
          </label>
          {form.loanEnabled && <>
            {input("loanPrincipal", "Loan principal (₹)")}
            {input("loanRate", "Rate (% p.a.)", "number", "0.5")}
            {input("loanTenure", "Tenure (months)")}
            {input("loanFee", "Preclosure fee (%)", "number", "0.5")}
          </>}
          <button className="btn btn-primary" onClick={save} style={{ padding: "6px 16px" }}>Save</button>
          {saveMsg && <span style={{ fontSize: 11, color: "var(--pos-text)" }}>{saveMsg}</span>}
        </div>
      )}

      {!m.started ? (
        <div style={{ fontSize: 13, color: "var(--muted2)", padding: 20 }}>
          Plan starts on <b>{cfg.startDate}</b> — tracking begins with the first trades on or after that date.
          {m.loan && <> EMI of <b>{fmt(m.emi)}</b>/month starts with month 1; total interest if held all {m.loan.tenureMonths} months ≈ <b>{fmt(m.emi * m.loan.tenureMonths - m.loan.principal)}</b>.</>}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <Card label="Capital in hand" value={fmt(m.eq)} sub={`seed ${fmt(cfg.seedCapital)} + retained P&L`} />
            <Card label="Total P&L since start" value={fmtS(m.totalPnl)} accent={m.totalPnl >= 0 ? "var(--pos-text)" : "var(--neg)"}
              sub={`${m.nTrades} trades · ${m.nTrades ? Math.round(m.nWins * 100 / m.nTrades) : 0}% WR`} />
            <Card label="Banked (after EMIs)" value={fmt(m.bank)} sub={`withdrawn ${fmt(m.withdrawn)} · ${m.payouts} payout${m.payouts === 1 ? "" : "s"}`} />
            <Card label={`Target ${fmt(cfg.targetEarned)}`} value={`${(progress * 100).toFixed(1)}%`}
              sub={m.etaTarget ? `ETA at actual pace: ${m.etaTarget}` : "ETA: needs ≥5 trading days of data"} />
          </div>

          {m.loan && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <Card label="Loan outstanding" value={fmt(m.loanOsNow)} sub={`${m.emisPaid}/${m.loan.tenureMonths} EMIs paid · interest paid ${fmt(m.interestPaid)}`} />
              <Card label="EMI" value={`${fmt(m.emi)}/mo`} sub={`trading must clear this to stand still`} />
              <Card label="Foreclosure cost" value={fmt(m.fcCost)} sub={`outstanding + ${m.loan.preclosureFeePct}% fee`} />
              <Card label="Foreclosable?" value={m.loanOsNow <= 0 ? "CLOSED" : m.foreclosableNow ? "YES — banked covers it" : "Not yet"}
                accent={m.foreclosableNow || m.loanOsNow <= 0 ? "var(--pos-text)" : "var(--warn-text)"}
                sub={m.loanOsNow <= 0 ? "loan fully repaid" : m.foreclosableNow ? `bank ${fmt(m.bank)} ≥ ${fmt(m.fcCost)}` : m.etaForeclose ? `ETA at actual pace: ${m.etaForeclose}` : "ETA: needs ≥5 trading days"} />
            </div>
          )}

          <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12.5, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span>This month: <b className={m.mPnl >= 0 ? "positive" : "negative"}>{fmtS(m.mPnl)}</b></span>
            <span>Pending withdrawal: <b>{m.pendingWd > 0 ? fmt(m.pendingWd) : "₹0 (month in red)"}</b></span>
            {m.loan && m.emisPaid < m.loan.tenureMonths && <span>EMI due at close: <b>{fmt(m.emi)}</b></span>}
            <span>{m.daysLeft} trading day{m.daysLeft === 1 ? "" : "s"} to month close</span>
            <span>vs plan (equity+banked): <b className={m.planGap >= 0 ? "positive" : "negative"}>{fmtS(m.planGap)}</b></span>
          </div>

          <PlanChart series={m.series} />
          <div style={{ display: "flex", gap: 12, fontSize: 10, color: "var(--muted2)", margin: "4px 0 14px" }}>
            <span style={{ color: "#22c55e", fontWeight: 700 }}>— actual equity</span>
            <span style={{ color: "#a78bfa", fontWeight: 700 }}>-- planned equity</span>
            <span>(both after withdrawals)</span>
          </div>

          <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr>
                <th>Month</th><th>P&L</th><th>Withdrawn</th>{m.loan && <th>EMI</th>}<th>Banked</th><th>Equity end</th>{m.loan && <th>Loan o/s</th>}<th>Plan eq.</th><th>Δ vs plan</th>
              </tr></thead>
              <tbody>
                {m.ledger.map(r => (
                  <tr key={r.month + (r.running ? "-run" : "")}>
                    <td style={{ fontWeight: 600 }}>{monthLabel(r.month)}{r.running ? " (running)" : ""}</td>
                    <td className={r.pnl >= 0 ? "positive" : "negative"}>{fmtS(r.pnl)}</td>
                    <td>{r.wd == null ? "—" : r.wd > 0 ? fmt(r.wd) : "₹0"}</td>
                    {m.loan && <td>{r.running ? "due" : r.emi > 0 ? fmt(r.emi) : "—"}</td>}
                    <td>{fmt(r.bank)}</td>
                    <td>{fmt(r.eqEnd)}</td>
                    {m.loan && <td style={{ color: "var(--muted2)" }}>{fmt(r.loanOs)}</td>}
                    <td style={{ color: "var(--muted2)" }}>{fmt(r.planEq)}</td>
                    <td className={r.eqEnd - r.planEq >= 0 ? "positive" : "negative"}>{fmtS(r.eqEnd - r.planEq)}</td>
                  </tr>
                ))}
                {!m.ledger.length && <tr><td colSpan={m.loan ? 9 : 7} style={{ color: "var(--muted2)" }}>No trading days yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 11.5, color: "var(--dim2)", marginTop: 10 }}>
            Sizing guide: equity is <b>{sizeMult.toFixed(2)}×</b> seed → compounding pace implies ≈ <b>{Math.max(1, Math.round(5 * sizeMult))} / {Math.max(1, Math.round(5 * sizeMult))} / {Math.max(1, Math.round(10 * sizeMult))}</b> lots (NIFTY / BANKNIFTY / SENSEX; base 5/5/10). Informational only — lot config never changes automatically.
          </div>
          <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 6, lineHeight: 1.5 }}>
            All figures derive from the live trade book (all strategies, P&L booked on exit day). Withdrawals are virtual: {Math.round(cfg.withdrawPct * 100)}% of a positive month moves to the bank at month close{m.loan ? "; the EMI is then paid from the bank (shortfall comes out of trading capital). “Foreclosable” means banked cash alone covers outstanding + fee — trading capital untouched." : "."} Tracker only — not investment advice; trading borrowed capital risks owing EMIs on lost money.
          </div>
        </>
      )}
    </div>
  );
}
